import {assert} from 'Asserts';
import SpreadsheetBulkImportStore from 'bulk-import/SpreadsheetBulkImportStore';
import BulkAntibodyStore from 'bulk-workflows/antibody/BulkAntibodyStore';
import BulkAssemblyStore from 'bulk-workflows/assembly/BulkAssemblyStore';
import BulkTranslationStore from 'bulk-workflows/translation/BulkTranslationStore';
import CalendarStore from 'calendar/CalendarStore';
import FileBrowserStore from 'file-browser/FileBrowserStore';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import InventoryFileStore from 'inventory/InventoryFileStore';
import Store from 'lib/Store';
import ProteinAlignmentStore from 'molbio/aa-sequences/AaSequenceAlignmentStore';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import CRISPRAssemblyStore from 'molbio/crispr/assembly/CRISPRAssemblyStore';
import CRISPRAnalysisStore from 'molbio/crispr/CRISPRAnalysisStore';
import HRTemplateStore from 'molbio/crispr/hr-template/HRTemplateStore';
import OrganismStore from 'molbio/genome/organism/OrganismStore';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import NavStore from 'nav/NavStore';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import ToastStore from 'nav/toasts/ToastStore';
import NoteStore from 'notebook/bioentities/NoteStore';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import EditorStore from 'notebook/protocols/stores/EditorStore';
import TimerStore from 'notebook/protocols/timers/TimerStore';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import TFIStore from 'notebook/tfi/TFIStore';
import NewUserFlowStore from 'onboarding/new-user-flow/NewUserFlowStore';
import RegistryActivationFlowStore from 'onboarding/registry-activation-flow/RegistryActivationFlowStore';
import PartyPageStore from 'orgs/PartyPageStore';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore, Thunk} from 'redux-stores/Store';
import RegistryStore from 'registry/RegistryStore';
import RegistryValidatorStore from 'registry/RegistryValidatorStore';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import AssayStore from 'results/AssayStore';
import ApplicableBatchesStore from 'samples/ApplicableBatchesStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import SampleStore from 'samples/SampleStore';
import StorableStore from 'samples/StorableStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import EntityStore from 'stores/EntityStore';
import EntryStore from 'stores/EntryStore';
import GenomeStore from 'stores/GenomeStore';
import WorkflowEntryTemplateStore from 'workflows/entry-templates/WorkflowEntryTemplateStore';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

export type Unsubscribe = () => void;

export default class StoreWrapper {
  // Subclasses should override this to indicate which stores to subscribe to.
  readonly stores: Array<Store> = [];
  readonly shouldSubscribeToRedux: boolean = true;
  // Subclasses should explicitly list the redux paths they want to subscribe to.
  // These paths MUST go beyond the initial models or ui objects, since these change too frequently.
  // By default, for backwards compatibility, this listens to all existing redux state at the time of creation
  // of this property, aside from a few specific sources.
  // Future redux state SHOULD NOT get added to this list.
  // Future DataConnectors should override this list with only the paths they care about.
  readonly reduxSubscriptionPaths: Array<string> = [
    'models.managed',
    'models.useradmin',
    'models.eln',
    'models.entrySummariesById',
    'models.fileSummariesById',
    'models.validationExtrasByBioentityId',
    'models.inaccessibleBatchIds',
    'models.inaccessibleEntryIds',
    'models.inaccessibleFileIds',
    'models.inaccessibleState',
    'models.inaccessibleStorableIds',
    'models.loadingState',
    'models.registrationRecordListsById',
    'models.collaboratorsByOwnableId',
    'models.policies',
    'models.procedures',
    'models.requests',
    'models.results',
    'models.reviews',
    'models.sampleGroups',
    'models.stars',
    'models.transferTables',
    'models.warehouse',
    'models.workflows',
    'ui.automationFileStatus',
    'ui.chemistry',
    'ui.codonOptimizationTab',
    'ui.collapsedTables',
    'ui.cookieConsentModal',
    'ui.dataTables',
    'ui.documentEditorLockState',
    'ui.export',
    'ui.formTableSwitcherState',
    'ui.globalClientStates',
    'ui.insights',
    'ui.menus',
    'ui.metadataTab',
    'ui.modbio',
    'ui.molbio',
    'ui.presence',
    'ui.requests',
    'ui.results',
    'ui.reviewCriteriaConfig',
    'ui.samples',
    'ui.schemaConfig',
    'ui.schemaTags',
    'ui.scrollableTables',
    'ui.structuredTableEditState',
    'ui.search',
    'ui.tenantAdmin',
    'ui.warehouse',
    'ui.workflowDetailsTable',
    'ui.workflowTemplateSelection',
    'ui.workflowVisualization',
    'ui.worklistSidepanelState',
  ];

  reduxState(): BenchlingState {
    assert(this.shouldSubscribeToRedux, 'Tried to access redux state without subscribing to redux.');
    return getStore().getState();
  }

  dispatch<T = void>(action: BenchlingAction | Thunk<T>): T {
    return getStore().dispatch(action);
  }

  applicableBatchesStore(): typeof ApplicableBatchesStore {
    return this.requiredStore(ApplicableBatchesStore);
  }

  assayStore(): typeof AssayStore {
    return this.requiredStore(AssayStore);
  }

  bulkAntibodyStore(): typeof BulkAntibodyStore {
    return this.requiredStore(BulkAntibodyStore);
  }

  bulkAssemblyStore(): typeof BulkAssemblyStore {
    return this.requiredStore(BulkAssemblyStore);
  }

  bulkTranslationStore(): typeof BulkTranslationStore {
    return this.requiredStore(BulkTranslationStore);
  }

  calendarStore(): typeof CalendarStore {
    return this.requiredStore(CalendarStore);
  }

  crisprAnalysisStore(): typeof CRISPRAnalysisStore {
    return this.requiredStore(CRISPRAnalysisStore);
  }

  crisprAssemblyStore(): typeof CRISPRAssemblyStore {
    return this.requiredStore(CRISPRAssemblyStore);
  }

  dataTableConfigStore(): typeof DataTableConfigStore {
    return this.requiredStore(DataTableConfigStore);
  }

  editorStore(): typeof EditorStore {
    return this.requiredStore(EditorStore);
  }

  entityStore(): typeof EntityStore {
    return this.requiredStore(EntityStore);
  }

  entryStore(): typeof EntryStore {
    return this.requiredStore(EntryStore);
  }

  fileBrowserStore(): typeof FileBrowserStore {
    return this.requiredStore(FileBrowserStore);
  }

  fullScreenStore(): typeof FullScreenStore {
    return this.requiredStore(FullScreenStore);
  }

  genomeStore(): typeof GenomeStore {
    return this.requiredStore(GenomeStore);
  }

  homeDashboardStore(): typeof HomeDashboardStore {
    return this.requiredStore(HomeDashboardStore);
  }

  hrTemplateStore(): typeof HRTemplateStore {
    return this.requiredStore(HRTemplateStore);
  }

  inventoryFileStore(): typeof InventoryFileStore {
    return this.requiredStore(InventoryFileStore);
  }

  organismStore(): typeof OrganismStore {
    return this.requiredStore(OrganismStore);
  }

  partyPageStore(): typeof PartyPageStore {
    return this.requiredStore(PartyPageStore);
  }

  navStore(): typeof NavStore {
    return this.requiredStore(NavStore);
  }

  newUserFlowStore(): typeof NewUserFlowStore {
    return this.requiredStore(NewUserFlowStore);
  }

  noteStore(): typeof NoteStore {
    return this.requiredStore(NoteStore);
  }

  proteinAlignmentStore(): typeof ProteinAlignmentStore {
    return this.requiredStore(ProteinAlignmentStore);
  }

  aaSequenceStore(): typeof AaSequenceStore {
    return this.requiredStore(AaSequenceStore);
  }

  registryActivationFlowStore(): typeof RegistryActivationFlowStore {
    return this.requiredStore(RegistryActivationFlowStore);
  }

  registryStore(): typeof RegistryStore {
    return this.requiredStore(RegistryStore);
  }

  registryValidatorStore(): typeof RegistryValidatorStore {
    return this.requiredStore(RegistryValidatorStore);
  }

  schemaFieldSelectorStore(): typeof SchemaFieldSelectorStore {
    return this.requiredStore(SchemaFieldSelectorStore);
  }

  sampleSchemaStore(): typeof SampleSchemaStore {
    return this.requiredStore(SampleSchemaStore);
  }

  sampleStore(): typeof SampleStore {
    return this.requiredStore(SampleStore);
  }

  sidepanelStore(): typeof SidepanelStore {
    return this.requiredStore(SidepanelStore);
  }

  spreadsheetBulkImportStore(): typeof SpreadsheetBulkImportStore {
    return this.requiredStore(SpreadsheetBulkImportStore);
  }

  spreadsheetStore(): typeof SpreadsheetStore {
    return this.requiredStore(SpreadsheetStore);
  }

  stageEntryStore(): typeof StageEntryStore {
    return this.requiredStore(StageEntryStore);
  }

  storableStore(): typeof StorableStore {
    return this.requiredStore(StorableStore);
  }

  subtemplateStore(): typeof SubtemplateStore {
    return this.requiredStore(SubtemplateStore);
  }

  templateStore(): typeof TemplateStore {
    return this.requiredStore(TemplateStore);
  }

  tfiStore(): typeof TFIStore {
    return this.requiredStore(TFIStore);
  }

  timerStore(): typeof TimerStore {
    return this.requiredStore(TimerStore);
  }

  toastStore(): typeof ToastStore {
    return this.requiredStore(ToastStore);
  }

  workflowEntryTemplateStore(): typeof WorkflowEntryTemplateStore {
    return this.requiredStore(WorkflowEntryTemplateStore);
  }

  workspaceStore(): typeof WorkspaceStore {
    return this.requiredStore(WorkspaceStore);
  }

  private requiredStore<T extends Store>(store: T): T {
    assert(this.stores.includes(store), 'Tried to access store without having listened to it.');
    return store;
  }

  getReduxChangeHandler(changeHandler: () => void): () => void {
    let currentState: BenchlingState = getStore().getState();
    return () => {
      const nextState: BenchlingState = getStore().getState();
      const previousState = currentState;
      currentState = nextState;

      if (nextState !== previousState) {
        if ((previousState && !nextState) || (!previousState && nextState)) {
          changeHandler();
          return;
        }
        for (const path of this.reduxSubscriptionPaths) {
          const parts = path.split('.');
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          let prevObj: any = previousState;
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          let nextObj: any = nextState;
          // Travel through the state objects until the end of the path
          // If any undefined or null state is found, bail out
          for (const part of parts) {
            if (!prevObj || typeof prevObj !== 'object' || !nextObj || typeof nextObj !== 'object') {
              break;
            }
            prevObj = prevObj[part];
            nextObj = nextObj[part];
          }
          // Compare the deepest level found for this path to see if they differ
          if (prevObj !== nextObj && (prevObj || nextObj)) {
            changeHandler();
            return;
          }
        }
      }
    };
  }

  subscribe(onStoreChange: () => void): Unsubscribe {
    for (const store of this.stores) {
      store.addChangeListener(onStoreChange);
    }
    let unsubscribeRedux: Unsubscribe | null = null;
    if (this.shouldSubscribeToRedux) {
      unsubscribeRedux = getStore().subscribe(this.getReduxChangeHandler(onStoreChange));
    }

    return () => {
      if (unsubscribeRedux) {
        unsubscribeRedux();
      }
      for (const store of this.stores) {
        store.removeChangeListener(onStoreChange);
      }
    };
  }
}
